import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const QuickActionToolbar = ({ section = 'publisher', selectedItems = [], onAction }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getActionsForSection = (section) => {
    switch (section) {
      case 'publisher':
        return [
          {
            id: 'submit_bulk',
            label: 'Bulk Submit',
            icon: 'Upload',
            shortcut: 'Ctrl+U',
            description: 'Submit multiple calls at once',
            variant: 'default'
          },
          {
            id: 'export_data',
            label: 'Export',
            icon: 'Download',
            shortcut: 'Ctrl+E',
            description: 'Export selected data',
            variant: 'outline'
          },
          {
            id: 'duplicate_campaign',
            label: 'Duplicate',
            icon: 'Copy',
            shortcut: 'Ctrl+D',
            description: 'Duplicate selected campaigns',
            variant: 'outline'
          },
          {
            id: 'archive_items',
            label: 'Archive',
            icon: 'Archive',
            shortcut: 'Del',
            description: 'Archive selected items',
            variant: 'secondary'
          }
        ];
      case 'buyer':
        return [
          {
            id: 'bulk_bid',
            label: 'Bulk Bid',
            icon: 'Zap',
            shortcut: 'Ctrl+B',
            description: 'Place bids on multiple leads',
            variant: 'default'
          },
          {
            id: 'update_profile',
            label: 'Update Profile',
            icon: 'User',
            shortcut: 'Ctrl+P',
            description: 'Update buyer profile settings',
            variant: 'outline'
          },
          {
            id: 'set_auto_bid',
            label: 'Auto Bid',
            icon: 'Bot',
            shortcut: 'Ctrl+A',
            description: 'Configure automatic bidding',
            variant: 'outline'
          },
          {
            id: 'export_leads',
            label: 'Export Leads',
            icon: 'Download',
            shortcut: 'Ctrl+E',
            description: 'Export lead data',
            variant: 'secondary'
          }
        ];
      case 'auction':
        return [
          {
            id: 'pause_auction',
            label: 'Pause',
            icon: 'Pause',
            shortcut: 'Space',
            description: 'Pause active auctions',
            variant: 'warning'
          },
          {
            id: 'force_close',
            label: 'Force Close',
            icon: 'Square',
            shortcut: 'Ctrl+X',
            description: 'Force close selected auctions',
            variant: 'destructive'
          },
          {
            id: 'extend_time',
            label: 'Extend',
            icon: 'Clock',
            shortcut: 'Ctrl+T',
            description: 'Extend auction time',
            variant: 'outline'
          },
          {
            id: 'broadcast_alert',
            label: 'Alert',
            icon: 'Megaphone',
            shortcut: 'Ctrl+M',
            description: 'Send system alert',
            variant: 'secondary'
          }
        ];
      case 'analytics':
        return [
          {
            id: 'generate_report',
            label: 'Generate Report',
            icon: 'FileText',
            shortcut: 'Ctrl+R',
            description: 'Generate custom report',
            variant: 'default'
          },
          {
            id: 'schedule_report',
            label: 'Schedule',
            icon: 'Calendar',
            shortcut: 'Ctrl+S',
            description: 'Schedule automated reports',
            variant: 'outline'
          },
          {
            id: 'export_chart',
            label: 'Export Chart',
            icon: 'Image',
            shortcut: 'Ctrl+I',
            description: 'Export chart as image',
            variant: 'outline'
          },
          {
            id: 'share_dashboard',
            label: 'Share',
            icon: 'Share2',
            shortcut: 'Ctrl+Shift+S',
            description: 'Share dashboard link',
            variant: 'secondary'
          }
        ];
      default:
        return [];
    }
  };

  const actions = getActionsForSection(section);
  const primaryActions = actions?.slice(0, 3);
  const secondaryActions = actions?.slice(3);

  const handleAction = (actionId) => {
    if (onAction) {
      onAction(actionId, selectedItems);
    }
  };

  const handleKeyboardShortcut = (event) => {
    const action = actions?.find(action => {
      const shortcut = action?.shortcut?.toLowerCase();
      const key = event?.key?.toLowerCase();
      const ctrlKey = event?.ctrlKey || event?.metaKey;
      const shiftKey = event?.shiftKey;

      if (shortcut?.includes('ctrl+shift+')) {
        const targetKey = shortcut?.replace('ctrl+shift+', '');
        return ctrlKey && shiftKey && key === targetKey;
      } else if (shortcut?.includes('ctrl+')) {
        const targetKey = shortcut?.replace('ctrl+', '');
        return ctrlKey && !shiftKey && key === targetKey;
      } else if (shortcut === 'space') {
        return key === ' ';
      } else if (shortcut === 'del') {
        return key === 'delete';
      }
      return false;
    });

    if (action) {
      event?.preventDefault();
      handleAction(action?.id);
    }
  };

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => document.removeEventListener('keydown', handleKeyboardShortcut);
  }, [actions]);

  if (actions?.length === 0) return null;

  return (
    <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg shadow-card">
      <div className="flex items-center space-x-2">
        <Icon name="Zap" size={16} className="text-accent" />
        <span className="text-sm font-medium text-foreground">Quick Actions</span>
        {selectedItems?.length > 0 && (
          <span className="px-2 py-0.5 bg-accent/10 text-accent text-xs rounded-full">
            {selectedItems?.length} selected
          </span>
        )}
      </div>
      <div className="flex items-center space-x-2">
        {/* Primary Actions */}
        {primaryActions?.map((action) => (
          <Button
            key={action?.id}
            variant={action?.variant}
            size="sm"
            onClick={() => handleAction(action?.id)}
            iconName={action?.icon}
            iconPosition="left"
            className="hidden sm:flex"
            title={`${action?.description} (${action?.shortcut})`}
          >
            {action?.label}
          </Button>
        ))}

        {/* Mobile Primary Action */}
        <Button
          variant="default"
          size="sm"
          onClick={() => handleAction(primaryActions?.[0]?.id)}
          iconName={primaryActions?.[0]?.icon}
          className="sm:hidden"
          title={primaryActions?.[0]?.description}
        >
          {primaryActions?.[0]?.label}
        </Button>

        {/* Secondary Actions Dropdown */}
        {secondaryActions?.length > 0 && (
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              iconName="MoreHorizontal"
              title="More actions"
            />
            
            {isExpanded && (
              <>
                <div 
                  className="fixed inset-0 z-1000"
                  onClick={() => setIsExpanded(false)}
                />
                <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-lg shadow-modal z-1100">
                  <div className="py-1">
                    {secondaryActions?.map((action) => (
                      <button
                        key={action?.id}
                        onClick={() => {
                          handleAction(action?.id);
                          setIsExpanded(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                        title={action?.description}
                      >
                        <div className="flex items-center space-x-2">
                          <Icon name={action?.icon} size={16} />
                          <span>{action?.label}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {action?.shortcut}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Keyboard Shortcuts Help */}
        <Button
          variant="ghost"
          size="sm"
          iconName="Keyboard"
          title="View keyboard shortcuts"
          className="text-muted-foreground"
        />
      </div>
    </div>
  );
};

export default QuickActionToolbar;